import { labelSpecForDesign, normalizeQrValue } from "./qrLabels.js";
import { resolveWallDecorSize, resolveWallDecorTheme } from "./wallDecor.js";
import { resolveMapDecorMap, resolveMapDecorSize } from "./mapDecor.js";
import { flagDecorDimensions, resolveFlagDecorFlag, resolveFlagDecorSize } from "./flagDecor.js";

const allowedAlignments = new Set(["left", "center", "right"]);
const allowedIconPositions = new Set(["left", "right", "none"]);
const allowedFieldTypes = new Set(["text", "textarea", "number", "select", "radio", "checkbox", "file"]);

export const LABEL_LINE_MAX_CHARACTERS = 32;

export function getPrimaryTextLimit(template, design = {}) {
  const hasExtraLines = [design.line2, design.line3, design.secondaryText].some((line) => String(line || "").trim());
  const configured = hasExtraLines ? template.primaryMaxCharactersWithExtraLines : template.maxCharacters;
  return Math.max(8, Math.round(Number(configured || LABEL_LINE_MAX_CHARACTERS)));
}

export function getPrimaryTextLines(design = {}, template = {}) {
  const text = String(design.text || "").replace(/\s+/g, " ").trim();
  if (!text) return [];
  if (template.autoWrapPrimary === false) return [text];
  const maxPerLine = Math.max(8, Math.round(Number(template.primaryWrapMaxCharacters || 16)));
  if (text.length <= maxPerLine) return [text];
  return wrapBalancedText(text, maxPerLine);
}

export function createId(prefix = "id") {
  return `${prefix}_${crypto.randomUUID().replace(/-/g, "").slice(0, 16)}`;
}

export function normalizeHex(value, fallback = "#000000") {
  const normalized = String(value ?? "").trim();
  return /^#[0-9a-f]{6}$/i.test(normalized) ? normalized.toUpperCase() : fallback;
}

export function clampNumber(value, min, max, fallback = min) {
  const number = Number(value);
  return Number.isFinite(number) ? Math.max(min, Math.min(max, number)) : fallback;
}

export function getLabelLineLayout(lineCount) {
  const count = Math.max(1, Math.min(4, Math.round(Number(lineCount) || 1)));
  if (count === 1) return { primaryMaxHeightRatio: 0.62, secondaryMaxHeightRatio: 0, gapRatio: 0 };
  if (count === 2) return { primaryMaxHeightRatio: 0.47, secondaryMaxHeightRatio: 0.36, gapRatio: 0.025 };
  if (count === 3) return { primaryMaxHeightRatio: 0.32, secondaryMaxHeightRatio: 0.28, gapRatio: 0.02 };
  return { primaryMaxHeightRatio: 0.26, secondaryMaxHeightRatio: 0.21, gapRatio: 0.018 };
}

export function getLabelScaleConfig(template = {}) {
  const textMin = clampNumber(template.minTextScale, 0.5, 1.2, 0.7);
  const textMax = Math.max(textMin, clampNumber(template.maxTextScale, textMin, 1.8, 1.25));
  const iconMin = clampNumber(template.minIconScale, 0.5, 1.2, 0.65);
  const iconMax = Math.max(iconMin, clampNumber(template.maxIconScale, iconMin, 2, 1.25));
  return {
    textMin,
    textMax,
    textDefault: clampNumber(template.defaultTextScale, textMin, textMax, 1),
    iconMin,
    iconMax,
    iconDefault: clampNumber(template.defaultIconScale, iconMin, iconMax, 1),
  };
}

export function resolveLabelLayers(template, design = {}) {
  const layers = template.layers ?? [];
  const fallback = {
    icon: { x: 0.74, y: 0.18, width: 0.2, height: 0.64 },
    primary: { x: 0.06, y: 0.18, width: 0.64, height: 0.32 },
    secondary: { x: 0.06, y: 0.52, width: 0.64, height: 0.34 },
  };
  const normalize = (layer, defaults) => layer ? {
    x: clampNumber(layer.xPct, 0, 100, defaults.x * 100) / 100,
    y: clampNumber(layer.yPct, 0, 100, defaults.y * 100) / 100,
    width: clampNumber(layer.widthPct, 1, 100, defaults.width * 100) / 100,
    height: clampNumber(layer.heightPct, 1, 100, defaults.height * 100) / 100,
  } : defaults;
  const iconLayer = layers.find((item) => item.type === "icon" && item.active !== false);
  const primaryLayer = layers.find((item) => item.role === "primaryText" && item.active !== false);
  const secondaryLayer = layers.find((item) => item.role === "secondaryText" && item.active !== false);
  const icon = normalize(iconLayer, fallback.icon);
  const primary = normalize(primaryLayer, fallback.primary);
  const secondary = normalize(secondaryLayer, fallback.secondary);
  const hasSecondary = [design.line2, design.line3, design.secondaryText].some((line) => String(line || "").trim());
  const hasIcon = Boolean((design.iconId || design.customerImage) && design.iconPosition !== "none");
  const safeInset = clampNumber(template.layout?.safeInsetPct, 2, 20, 6) / 100;
  const contentGap = clampNumber(template.layout?.contentGapPct, 1, 15, 4) / 100;
  if (!hasSecondary) primary.height = Math.max(primary.height, secondary.y + secondary.height - primary.y);
  if (design.iconPosition === "left") icon.x = Math.max(0, 1 - icon.x - icon.width);
  if (hasIcon) {
    const scale = getLabelScaleConfig(template);
    const requestedScale = clampNumber(design.iconScale, scale.iconMin, scale.iconMax, scale.iconDefault);
    const safeWidth = Math.max(0.01, 1 - safeInset * 2);
    const safeHeight = Math.max(0.01, 1 - safeInset * 2);
    const effectiveScale = Math.min(requestedScale, safeWidth / icon.width, safeHeight / icon.height);
    const centerX = icon.x + icon.width / 2;
    const centerY = icon.y + icon.height / 2;
    icon.width *= effectiveScale;
    icon.height *= effectiveScale;
    icon.x = clampNumber(centerX - icon.width / 2, safeInset, 1 - safeInset - icon.width, safeInset);
    icon.y = clampNumber(centerY - icon.height / 2, safeInset, 1 - safeInset - icon.height, safeInset);
  }
  const fitTextBesideIcon = (rect) => {
    if (!hasIcon) return { ...rect, x: safeInset, width: Math.max(0.2, 1 - safeInset * 2) };
    if (design.iconPosition === "left") {
      const x = Math.max(safeInset, icon.x + icon.width + contentGap);
      return { ...rect, x, width: Math.max(0.2, 1 - safeInset - x) };
    }
    const x = Math.max(safeInset, rect.x);
    return { ...rect, x, width: Math.max(0.2, Math.min(rect.width, icon.x - contentGap - x)) };
  };
  Object.assign(primary, fitTextBesideIcon(primary));
  Object.assign(secondary, fitTextBesideIcon(secondary));
  return { icon, primary, secondary, hasSecondary };
}

export function createDefaultResponses(template) {
  return Object.fromEntries((template.fields ?? []).filter((field) => field.active !== false).map((field) => [field.id, normalizeFieldValue(field, field.defaultValue)]));
}

export function evaluateRules(template, rawResponses = {}) {
  const responses = { ...createDefaultResponses(template), ...rawResponses };
  const fields = Object.fromEntries((template.fields ?? []).map((field) => [field.id, {
    ...field,
    visible: field.active !== false && !field.hiddenByDefault,
    requiredNow: Boolean(field.required),
    disabledNow: false,
  }]));
  const matchedRuleIds = [];
  let rulePriceDeltaNok = 0;

  for (const rule of template.rules ?? []) {
    if (rule.active === false || !matchesConditions(rule.all ?? [], responses, rule.match || "all")) continue;
    matchedRuleIds.push(rule.id);
    for (const action of rule.actions ?? []) {
      const target = fields[action.targetFieldId];
      if (action.type === "addPrice") {
        rulePriceDeltaNok += Number(action.amountNok || 0);
      } else if (target) {
        if (action.type === "show") target.visible = true;
        if (action.type === "hide") target.visible = false;
        if (action.type === "require") target.requiredNow = true;
        if (action.type === "optional") target.requiredNow = false;
        if (action.type === "disable") target.disabledNow = true;
        if (action.type === "enable") target.disabledNow = false;
        if (action.type === "setValue" && isEmpty(responses[target.id])) responses[target.id] = normalizeFieldValue(target, action.value);
      }
    }
  }

  return { fields, responses, matchedRuleIds, rulePriceDeltaNok };
}

export function calculatePrice(template, rawResponses = {}, sets = 1) {
  const runtime = evaluateRules(template, rawResponses);
  let optionDeltaNok = runtime.rulePriceDeltaNok;
  const adjustments = [];
  for (const field of Object.values(runtime.fields)) {
    if (!field.visible) continue;
    const value = runtime.responses[field.id];
    let amount = 0;
    if (field.type === "checkbox" && value === true) amount += Number(field.priceDeltaNok || 0);
    if (field.type === "select" || field.type === "radio") {
      amount += Number((field.options ?? []).find((option) => option.value === value)?.priceDeltaNok || 0);
    }
    if (field.type === "file" && value?.dataUrl) amount += Number(field.priceDeltaNok || 0);
    if (amount) {
      optionDeltaNok += amount;
      adjustments.push({ fieldId: field.id, label: field.label, amountNok: amount });
    }
  }
  if (runtime.rulePriceDeltaNok) adjustments.push({ fieldId: "rules", label: "Regeljustering", amountNok: runtime.rulePriceDeltaNok });
  const normalizedSets = Math.round(clampNumber(sets, 1, 20, 1));
  const pricePerSetNok = Number(template.basePriceNok || 0) + optionDeltaNok;
  const tier = [...(template.discountTiers ?? [])].sort((a, b) => b.minSets - a.minSets).find((item) => normalizedSets >= item.minSets);
  const discountPercent = Number(tier?.percent || 0);
  const campaignDiscountPercent = clampNumber(template.campaignDiscountPercent, 0, 90, 0);
  const beforeDiscountNok = pricePerSetNok * normalizedSets;
  const compareAtTotalNok = Math.round(beforeDiscountNok * (1 - discountPercent / 100) * 100) / 100;
  const totalNok = Math.round(compareAtTotalNok * (1 - campaignDiscountPercent / 100) * 100) / 100;
  return { currency: template.currency || "NOK", sets: normalizedSets, pricePerSetNok, optionDeltaNok, beforeDiscountNok, compareAtTotalNok, discountPercent, campaignDiscountPercent, totalNok, adjustments, matchedRuleIds: runtime.matchedRuleIds };
}

export function validateResponses(template, rawResponses = {}) {
  const runtime = evaluateRules(template, rawResponses);
  const responses = {};
  const errors = {};
  for (const field of Object.values(runtime.fields)) {
    if (!field.visible || field.active === false) continue;
    const value = normalizeFieldValue(field, runtime.responses[field.id]);
    responses[field.id] = value;
    if (field.requiredNow && isEmpty(value)) errors[field.id] = `${field.label} må fylles ut.`;
    if ((field.type === "text" || field.type === "textarea") && value) {
      if (field.minLength && value.length < field.minLength) errors[field.id] = `${field.label} må ha minst ${field.minLength} tegn.`;
      if (field.maxLength && value.length > field.maxLength) errors[field.id] = `${field.label} kan ha maks ${field.maxLength} tegn.`;
      if (field.pattern && !new RegExp(field.pattern).test(value)) errors[field.id] = field.patternMessage || `${field.label} har ugyldig format.`;
    }
    if (field.type === "number" && value !== "") {
      if (field.min != null && value < field.min) errors[field.id] = `${field.label} må være minst ${field.min}.`;
      if (field.max != null && value > field.max) errors[field.id] = `${field.label} kan være maks ${field.max}.`;
    }
    if ((field.type === "select" || field.type === "radio") && value && !(field.options ?? []).some((option) => option.value === value)) {
      errors[field.id] = `${field.label} inneholder et ugyldig valg.`;
    }
    if (field.type === "file" && value?.dataUrl) validateUpload(field, value, errors);
  }
  return { ok: Object.keys(errors).length === 0, errors, responses, runtime };
}

export function normalizeDesign(input, template, state) {
  if (!input || typeof input !== "object" || Array.isArray(input)) throw validationError("Design mangler.");
  const isQrDesign = template.outputLayout === "qr-label-sheet";
  const isWallDecor = template.outputLayout === "wall-decor-sheet";
  const isMapDecor = template.outputLayout === "map-decor-sheet";
  const isFlagDecor = template.outputLayout === "flag-decor-sheet";
  let text = isQrDesign
    ? normalizeQrValue(input.text)
    : String(input.text ?? "").replace(/\s+/g, " ").trim();
  if (!text && !isWallDecor && !isMapDecor && !isFlagDecor) throw validationError(isQrDesign ? "Skriv inn innholdet som QR-koden skal åpne." : "Skriv inn et navn.");
  const secondaryMaxCharacters = Math.max(8, Math.round(Number(template.secondaryMaxCharacters || LABEL_LINE_MAX_CHARACTERS)));
  const line2 = normalizeExtraLine(input.line2, isQrDesign ? "Linjen over QR-koden" : "Linje 2", secondaryMaxCharacters);
  const line3 = normalizeExtraLine(input.line3, isQrDesign ? "Linjen under QR-koden" : "Linje 3", secondaryMaxCharacters);
  const primaryLimit = getPrimaryTextLimit(template, { ...input, line2, line3 });
  if (!isWallDecor && !isMapDecor && !isFlagDecor && text.length > primaryLimit) throw validationError(`${isQrDesign ? "QR-innholdet" : "Teksten"} kan ha maks ${primaryLimit} tegn${!isQrDesign && (line2 || line3) ? " når flere linjer brukes" : ""}.`);

  const font = state.assets.fonts.find((item) => item.id === input.fontId && item.active);
  const background = state.assets.colors.find((item) => item.id === input.backgroundColorId && item.active && item.usage.includes("background"));
  const textColor = state.assets.colors.find((item) => item.id === input.textColorId && item.active && item.usage.includes("text"));
  const icon = state.assets.icons.find((item) => item.id === input.iconId && item.active);
  if (!font || !background || !textColor) throw validationError("Designet inneholder et valg som ikke er tilgjengelig.");
  const validation = validateResponses(template, input.responses ?? {});
  if (!validation.ok) throw validationError(Object.values(validation.errors)[0]);
  const themeField = (template.fields ?? []).find((field) => ["labelTheme", "wallDecorTheme", "mapDecorMap", "flagDecorFlag"].includes(field.role));
  const selectedTheme = themeField?.options?.find((option) => option.value === validation.responses[themeField.id]);
  if (isWallDecor) text = selectedTheme?.label || "Veggdekor";
  if (isMapDecor) text = selectedTheme?.label || "Veggkart";
  if (isFlagDecor) text = selectedTheme?.label || "Veggflagg";
  const backgroundIsCustomerControlled = !selectedTheme || selectedTheme.allowsBackgroundColor === true;
  if (!isWallDecor && !isMapDecor && !isFlagDecor && backgroundIsCustomerControlled && colorContrastRatio(background.hex, textColor.hex) < 4.5) {
    throw validationError("Velg en tekstfarge med tydeligere kontrast mot bakgrunnen.");
  }
  const customerImageField = (template.fields ?? []).find((field) => field.role === "customerImage");
  const secondaryTextField = (template.fields ?? []).find((field) => field.role === "secondaryText");
  const customerImage = customerImageField ? validation.responses[customerImageField.id] || null : null;
  const pricing = calculatePrice(template, validation.responses, input.sets);
  const iconPosition = allowedIconPositions.has(input.iconPosition) ? input.iconPosition : template.defaultIconPosition;
  const scaleConfig = getLabelScaleConfig(template);

  return {
    version: 2,
    templateId: template.id,
    templateVersionId: template.templateVersionId || null,
    productBindingId: template.productBindingId || null,
    platform: template.platform || "shopify",
    storeId: template.storeId || null,
    productHandle: template.productHandle,
    text,
    line2,
    line3,
    secondaryText: secondaryTextField ? String(validation.responses[secondaryTextField.id] || "") : "",
    fontId: font.id,
    backgroundColorId: background.id,
    textColorId: textColor.id,
    iconId: icon?.id ?? null,
    customerImage,
    iconPosition: (icon || customerImage) ? iconPosition : "none",
    alignment: allowedAlignments.has(input.alignment) ? input.alignment : "center",
    textScale: clampNumber(input.textScale, scaleConfig.textMin, scaleConfig.textMax, scaleConfig.textDefault),
    iconScale: clampNumber(input.iconScale, scaleConfig.iconMin, scaleConfig.iconMax, scaleConfig.iconDefault),
    sets: pricing.sets,
    responses: validation.responses,
    labelSpec: labelSpecForDesign(template, { responses: validation.responses }),
    pricing,
    assetSnapshot: {
      font: snapshotAsset(font),
      background: snapshotAsset(background),
      textColor: snapshotAsset(textColor),
      icon: snapshotAsset(icon),
    },
    previewDataUrl: typeof input.previewDataUrl === "string" ? input.previewDataUrl.slice(0, 600_000) : "",
    createdAt: new Date().toISOString(),
  };
}

export function colorContrastRatio(firstHex, secondHex) {
  const luminance = (hex) => {
    const value = String(hex || "").trim().replace(/^#/, "");
    if (!/^[0-9a-f]{6}$/i.test(value)) return 0;
    const channels = [0, 2, 4].map((offset) => Number.parseInt(value.slice(offset, offset + 2), 16) / 255)
      .map((channel) => channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4);
    return channels[0] * 0.2126 + channels[1] * 0.7152 + channels[2] * 0.0722;
  };
  const first = luminance(firstHex);
  const second = luminance(secondHex);
  return (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05);
}

export function resolveProductBinding(state, productKey, platform = "shopify", storeKey = "") {
  const key = String(productKey || "").trim();
  const normalizedStoreKey = normalizeStoreKey(storeKey);
  return (state.productBindings ?? []).find((binding) => binding.active !== false
    && (binding.platform || "shopify") === platform
    && (!normalizedStoreKey || storeMatches(state, binding.storeId, normalizedStoreKey))
    && [binding.externalProductHandle, binding.externalProductId, binding.sku, binding.id].filter(Boolean).includes(key)) || null;
}

export function sanitizePublicState(state, productKey, platform = "shopify", storeKey = "") {
  const binding = resolveProductBinding(state, productKey, platform, storeKey);
  const version = binding?.publishedVersionId
    ? (state.templateVersions ?? []).find((item) => item.id === binding.publishedVersionId && item.status === "published")
    : null;
  const template = version?.snapshot
    ? { ...structuredClone(version.snapshot), productHandle: binding.externalProductHandle, templateVersionId: version.id, productBindingId: binding.id, platform: binding.platform, storeId: binding.storeId }
    : state.templates.find((item) => item.productHandle === productKey && ["ready", "published"].includes(item.status));
  if (!template) return null;
  const allowed = (ids, values, usage = null) => ids.map((id) => values.find((item) => item.id === id && item.active && (!usage || item.usage?.includes(usage)))).filter(Boolean);
  return {
    version: state.version,
    binding: binding ? {
      id: binding.id,
      platform: binding.platform,
      storeId: binding.storeId,
      externalProductId: binding.externalProductId,
      externalProductHandle: binding.externalProductHandle,
      sku: binding.sku,
      publishedVersionId: binding.publishedVersionId,
    } : null,
    template,
    assets: {
      fonts: allowed(template.fontIds, state.assets.fonts),
      backgrounds: allowed(template.backgroundColorIds, state.assets.colors, "background"),
      textColors: allowed(template.textColorIds, state.assets.colors, "text"),
      icons: allowed(template.iconIds, state.assets.icons),
    },
  };
}

function normalizeStoreKey(value) {
  return String(value || "").trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/.*$/, "");
}

function storeMatches(state, storeId, storeKey) {
  const store = (state.stores ?? []).find((item) => item.id === storeId);
  if (!store) return false;
  return [store.id, store.domain, store.shopDomain].map(normalizeStoreKey).includes(storeKey);
}

export function createPublishedTemplateVersion(template, existingVersions = []) {
  const number = Math.max(0, ...existingVersions.filter((item) => item.templateId === template.id).map((item) => Number(item.number) || 0)) + 1;
  const publishedAt = new Date().toISOString();
  const snapshot = structuredClone(template);
  snapshot.status = "published";
  delete snapshot.draftUpdatedAt;
  return {
    id: createId("version"),
    templateId: template.id,
    number,
    status: "published",
    publishedAt,
    snapshot,
  };
}

export function summarizeDesign(design, state) {
  const template = state.templates.find((item) => item.id === design.templateId);
  if (template?.outputLayout === "qr-label-sheet") {
    const spec = labelSpecForDesign(template, design);
    return [`QR: ${design.text}`, design.line2 ? `Over: ${design.line2}` : null, design.line3 ? `Under: ${design.line3}` : null, `${spec.widthMm} × ${spec.heightMm} mm`, `${spec.labelsPerSet} lapper`].filter(Boolean).join(" · ");
  }
  if (template?.outputLayout === "wall-decor-sheet") {
    const theme = resolveWallDecorTheme(template, design);
    const size = resolveWallDecorSize(template, design);
    return [theme?.label || "Veggdekor", size?.label, size?.spreadLabel, `${theme?.elementCount || 1} løse elementer`].filter(Boolean).join(" · ");
  }
  if (template?.outputLayout === "map-decor-sheet") {
    const map = resolveMapDecorMap(template, design);
    const size = resolveMapDecorSize(template, design);
    return [map?.label || "Veggkart", size?.label, size?.spreadLabel, `${size?.panelCount || 1} ${size?.panelCount === 1 ? "panel" : "paneler"}`].filter(Boolean).join(" · ");
  }
  if (template?.outputLayout === "flag-decor-sheet") {
    const flag = resolveFlagDecorFlag(template, design);
    const size = flagDecorDimensions(flag, resolveFlagDecorSize(template, design));
    return [flag?.label || "Veggflagg", size.label, size.spreadLabel, `${size.panelCount || 1} ${size.panelCount === 1 ? "panel" : "paneler"}`].filter(Boolean).join(" · ");
  }
  const find = (list, id) => list.find((item) => item.id === id)?.name ?? "-";
  const extras = Object.entries(design.responses ?? {}).filter(([, value]) => !isEmpty(value) && typeof value !== "object").length;
  const textSummary = [design.text, design.line2, design.line3].filter(Boolean).join(" / ");
  return [textSummary, find(state.assets.fonts, design.fontId), find(state.assets.colors, design.backgroundColorId), find(state.assets.colors, design.textColorId), design.customerImage ? "Eget bilde" : design.iconId ? find(state.assets.icons, design.iconId) : "Uten ikon", extras ? `${extras} ekstravalg` : null].filter(Boolean).join(" · ");
}

export function buildShopifyLineItemProperties(design, state) {
  const template = state.templates.find((item) => item.id === design.templateId);
  const font = design.assetSnapshot?.font || state.assets.fonts.find((item) => item.id === design.fontId);
  const background = design.assetSnapshot?.background || state.assets.colors.find((item) => item.id === design.backgroundColorId);
  const textColor = design.assetSnapshot?.textColor || state.assets.colors.find((item) => item.id === design.textColorId);
  const icon = design.assetSnapshot?.icon || state.assets.icons.find((item) => item.id === design.iconId);
  const surface = (template?.fields ?? [])
    .filter((field) => ["labelTheme", "backgroundPattern", "mapDecorMap", "flagDecorFlag"].includes(field.role))
    .map((field) => field.options?.find((option) => option.value === design.responses?.[field.id]))
    .filter((option) => option?.imageUrl)
    .at(-1);
  const lines = [design.text, design.line2, design.line3, design.secondaryText].map((line) => String(line || "").trim()).filter(Boolean);
  if (template?.outputLayout === "qr-label-sheet") {
    const spec = labelSpecForDesign(template, design);
    return {
      "QR-innhold": design.text.slice(0, 240),
      "Linje over QR-koden": design.line2 || "Uten tekst",
      "Linje under QR-koden": design.line3 || "Uten tekst",
      "Størrelse": `${spec.label} · ${spec.widthMm} × ${spec.heightMm} mm`,
      "Antall lapper per sett": String(spec.labelsPerSet),
      "Bakgrunnsfarge": background?.name || "Standard",
      "Tekstfarge": textColor?.name || "Standard",
      "_pplr_preview": "Forhåndsvisning",
    };
  }
  if (template?.outputLayout === "wall-decor-sheet") {
    const theme = resolveWallDecorTheme(template, design);
    const size = resolveWallDecorSize(template, design);
    return {
      "Motiv": theme?.label || design.text || "Veggdekor",
      "Størrelse": `${size?.label || "Standard"} · ${size?.spreadLabel || ""}`.replace(/ · $/, ""),
      "Printformat": `${Number(size?.widthMm || 0) / 10} × ${Math.round(Number(size?.heightMm || 0) / 10)} cm`,
      "Antall dekorelementer": String(theme?.elementCount || 1),
      "Egnet for tak": theme?.ceilingSuitable ? "Ja" : "Nei",
      "_pplr_preview": "Forhåndsvisning",
    };
  }
  if (template?.outputLayout === "map-decor-sheet") {
    const map = resolveMapDecorMap(template, design);
    const size = resolveMapDecorSize(template, design);
    return {
      "Kart": map?.label || design.text || "Veggkart",
      "Størrelse": `${size?.label || "Standard"} · ${size?.spreadLabel || ""}`.replace(/ · $/, ""),
      "Ferdig mål": size?.spreadLabel || "",
      "Paneler": String(size?.panelCount || 1),
      "Kartgrunnlag": map?.sourceName || "Natural Earth 1:10m",
      "Språk": map?.labelsLanguage === "nb-NO" ? "Norsk" : (map?.labelsLanguage || "Norsk"),
      "_pplr_preview": "Forhåndsvisning",
    };
  }
  if (template?.outputLayout === "flag-decor-sheet") {
    const flag = resolveFlagDecorFlag(template, design);
    const size = flagDecorDimensions(flag, resolveFlagDecorSize(template, design));
    return {
      "Flagg": flag?.label || design.text || "Veggflagg",
      "Landkode": flag?.isoCode || "",
      "Størrelse": `${size.label || "Standard"} · ${size.spreadLabel || ""}`.replace(/ · $/, ""),
      "Ferdig mål": size.spreadLabel || "",
      "Paneler": String(size.panelCount || 1),
      "Sideforhold": `${flag?.ratioWidth || 3}:${flag?.ratioHeight || 2}`,
      "Kildelisens": flag?.licenseName || "Public domain",
      "_pplr_preview": "Forhåndsvisning",
    };
  }
  return {
    "Skriv inn tekst": lines.join(" ").slice(0, 240),
    "Velg skrifttype": font?.name || "Standard",
    "Velg skriftfarge": textColor?.name || "Standard",
    "Bakgrunnsfarge": background?.name || "Standard",
    "Bakgrunn": surface?.label || "Fjern Bakgrunn",
    "Ikon": design.customerImage?.name ? `Eget bilde: ${design.customerImage.name}` : icon?.name || "Uten ikon",
    "_pplr_preview": "Forhåndsvisning",
  };
}

function normalizeExtraLine(value, label, maxCharacters = LABEL_LINE_MAX_CHARACTERS) {
  const normalized = String(value ?? "").replace(/\s+/g, " ").trim();
  if (normalized.length > maxCharacters) throw validationError(`${label} kan ha maks ${maxCharacters} tegn.`);
  return normalized;
}

function wrapBalancedText(text, maxPerLine) {
  const words = text.split(" ").filter(Boolean);
  let best = null;
  for (let index = 1; index < words.length; index += 1) {
    const first = words.slice(0, index).join(" ");
    const second = words.slice(index).join(" ");
    if (first.length > maxPerLine || second.length > maxPerLine) continue;
    const score = Math.abs(first.length - second.length);
    if (!best || score < best.score) best = { score, lines: [first, second] };
  }
  if (best) return best.lines;
  const splitAt = Math.min(maxPerLine, Math.max(1, Math.ceil(text.length / 2)));
  return [text.slice(0, splitAt).trim(), text.slice(splitAt).trim()].filter(Boolean);
}

function matchesConditions(conditions, responses, match = "all") {
  const evaluate = (condition) => {
    const actual = responses[condition.fieldId];
    const expected = condition.value;
    if (condition.operator === "notEquals") return String(actual) !== String(expected);
    if (condition.operator === "contains") return String(actual ?? "").toLowerCase().includes(String(expected ?? "").toLowerCase());
    if (condition.operator === "greaterThan") return Number(actual) > Number(expected);
    if (condition.operator === "lessThan") return Number(actual) < Number(expected);
    if (condition.operator === "isEmpty") return isEmpty(actual);
    if (condition.operator === "isNotEmpty") return !isEmpty(actual);
    if (typeof expected === "boolean") return actual === expected || String(actual) === String(expected);
    return String(actual ?? "") === String(expected ?? "");
  };
  return match === "any" ? conditions.some(evaluate) : conditions.every(evaluate);
}

function normalizeFieldValue(field, value) {
  if (!allowedFieldTypes.has(field.type)) return "";
  if (field.type === "checkbox") return value === true || value === "true";
  if (field.type === "number") return value === "" || value == null ? "" : Number(value);
  if (field.type === "file") return value && typeof value === "object" ? {
    name: String(value.name || "opplasting").slice(0, 120),
    mimeType: String(value.mimeType || ""),
    size: Number(value.size || 0),
    width: Number(value.width || 0),
    height: Number(value.height || 0),
    dataUrl: String(value.dataUrl || ""),
  } : null;
  return String(value ?? "").trim();
}

function validateUpload(field, upload, errors) {
  const allowedMime = field.acceptMime ?? ["image/png", "image/jpeg"];
  const match = upload.dataUrl.match(/^data:([^;,]+);base64,([a-z0-9+/=]+)$/i);
  if (!match || !allowedMime.includes(match[1])) errors[field.id] = `${field.label} må være PNG eller JPG.`;
  const approximateBytes = match ? Math.floor(match[2].length * 0.75) : Infinity;
  if (approximateBytes > Number(field.maxFileBytes || 2_000_000)) errors[field.id] = `${field.label} er for stor.`;
  if (field.minWidth && upload.width && upload.width < field.minWidth) errors[field.id] = `${field.label} må være minst ${field.minWidth} px bred.`;
  if (field.minHeight && upload.height && upload.height < field.minHeight) errors[field.id] = `${field.label} må være minst ${field.minHeight} px høy.`;
}

function isEmpty(value) {
  return value == null || value === "" || value === false || (typeof value === "object" && !value.dataUrl);
}

function validationError(message) {
  const error = new Error(message);
  error.statusCode = 400;
  return error;
}

function snapshotAsset(asset) {
  if (!asset) return null;
  const keys = ["id", "name", "hex", "cssFamily", "pdfFont", "dataUrl", "sourceUrl", "printSourceUrl", "mimeType", "svgPath", "viewBox", "fill"];
  return Object.fromEntries(keys.filter((key) => asset[key] != null && asset[key] !== "").map((key) => [key, asset[key]]));
}
